/** Per-user document templates — Tier 3 Group D Slice 1c.
 *
 *  User templates live in localStorage; the picker dialog surfaces
 *  them under a "My templates" tab alongside the four built-in
 *  categories. Save-as-template captures the active document's
 *  canvas size + DPI; restoring instantiates a blank canvas at
 *  those dimensions.
 *
 *  V1 scope: dimensions + DPI + name + category only. The roadmap
 *  calls for "full document JSON snapshot" — that's deferred to a
 *  v2 that wires through the existing `applySnapshot` /
 *  `PersistedSession` plumbing in `EditorPage`. The v1 data shape
 *  is forward-compatible: adding an optional `canvasJSON` field
 *  later doesn't break stored v1 records since `parse` filters by
 *  field presence.
 *
 *  Same in-memory + write-through-storage pattern user-palettes-
 *  prefs uses, for the same reason — tests stub `localStorage`
 *  with throw-away vi.fn()s, so the in-memory snapshot is the
 *  source of truth and storage is best-effort persistence. */

import { createPersistedNamespace, safeStorage } from './user-prefs-storage';
import type { DocumentTemplate } from './builtin-templates';

export interface UserTemplate {
  /** `user:<rand>` — never `builtin:*`. */
  id: string;
  /** Display name (renamed via the manage UI). */
  name: string;
  /** Always 'custom' for user-saved templates — they don't fit one
   *  of the built-in category buckets, so the dialog surfaces them
   *  under a dedicated "My templates" tab. */
  category: 'custom';
  width: number;
  height: number;
  /** Optional DPI. Carries through from `Image → Document Settings`
   *  if the user has configured it; absent for default web-pixel
   *  documents. */
  dpi?: number;
  /** Saved-at ms timestamp. Used for sort-by-recent ordering. */
  createdAt: number;
}

type StoredShape = { templates: UserTemplate[] };

type Listener = (state: StoredShape) => void;
const listeners = new Set<Listener>();

export function subscribeToUserTemplates(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function emit(state: StoredShape): void {
  listeners.forEach((l) => l(state));
}

const MAX_USER_TEMPLATES = 64;

const namespace = createPersistedNamespace<StoredShape>({
  storageKey: 'image-editor:user-templates:v1',
  defaultValue: { templates: [] },
  parse: (raw): StoredShape => {
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
      return { templates: [] };
    }
    const candidate = raw as Record<string, unknown>;
    const list = Array.isArray(candidate.templates) ? candidate.templates : [];
    const templates = list
      .map((t): UserTemplate | null => {
        if (typeof t !== 'object' || t === null) return null;
        const tt = t as Record<string, unknown>;
        if (typeof tt.id !== 'string' || !tt.id.startsWith('user:')) return null;
        if (typeof tt.name !== 'string') return null;
        if (typeof tt.width !== 'number' || tt.width <= 0) return null;
        if (typeof tt.height !== 'number' || tt.height <= 0) return null;
        const dpi = typeof tt.dpi === 'number' && tt.dpi > 0 ? tt.dpi : undefined;
        const createdAt = typeof tt.createdAt === 'number' ? tt.createdAt : Date.now();
        return {
          id: tt.id,
          name: tt.name,
          category: 'custom',
          width: Math.round(tt.width),
          height: Math.round(tt.height),
          dpi,
          createdAt,
        };
      })
      .filter((t): t is UserTemplate => t !== null)
      .slice(0, MAX_USER_TEMPLATES);
    return { templates };
  },
});

let inMemoryState: StoredShape = namespace.load();

function generateId(): string {
  // Crockford base32 alphabet, 10 random chars — matches the
  // user-palettes id strategy (no new dep, vanishing collision
  // odds at the per-user scale).
  const alphabet = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
  let out = 'user:';
  for (let i = 0; i < 10; i++) {
    out += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return out;
}

export function loadUserTemplates(_storage: Storage = safeStorage()): StoredShape {
  return inMemoryState;
}

function save(state: StoredShape, storage: Storage = safeStorage()): void {
  inMemoryState = state;
  namespace.save(state, storage);
  emit(state);
}

/** Persist a new user template captured from the current document.
 *  Returns the new state. */
export function createUserTemplate(
  input: { name: string; width: number; height: number; dpi?: number },
  storage: Storage = safeStorage(),
): StoredShape {
  const name = input.name.trim() || 'Untitled template';
  const state = inMemoryState;
  if (state.templates.length >= MAX_USER_TEMPLATES) return state;
  const next: UserTemplate = {
    id: generateId(),
    name,
    category: 'custom',
    width: Math.max(1, Math.round(input.width)),
    height: Math.max(1, Math.round(input.height)),
    dpi: input.dpi && input.dpi > 0 ? Math.round(input.dpi) : undefined,
    createdAt: Date.now(),
  };
  const updated: StoredShape = { templates: [next, ...state.templates] };
  save(updated, storage);
  return updated;
}

export function renameUserTemplate(
  id: string,
  name: string,
  storage: Storage = safeStorage(),
): StoredShape {
  const trimmed = name.trim();
  if (!trimmed) return inMemoryState;
  const state = inMemoryState;
  const next: StoredShape = {
    templates: state.templates.map((t) => (t.id === id ? { ...t, name: trimmed } : t)),
  };
  save(next, storage);
  return next;
}

export function deleteUserTemplate(id: string, storage: Storage = safeStorage()): StoredShape {
  const state = inMemoryState;
  const next: StoredShape = { templates: state.templates.filter((t) => t.id !== id) };
  save(next, storage);
  return next;
}

export function clearAllUserTemplates(storage: Storage = safeStorage()): void {
  inMemoryState = { templates: [] };
  namespace.clear(storage);
  emit({ templates: [] });
}

/** Convert a `UserTemplate` to the unified `DocumentTemplate` shape
 *  the picker dialog consumes (mixed with built-ins). */
export function userTemplateToDocumentTemplate(t: UserTemplate): DocumentTemplate {
  return {
    id: t.id,
    name: t.name,
    category: 'custom',
    kind: 'user',
    width: t.width,
    height: t.height,
    dpi: t.dpi,
  };
}

export const USER_TEMPLATES_LIMITS = {
  maxTemplates: MAX_USER_TEMPLATES,
};

// Test-only handles
export function __resetUserTemplatesInMemoryForTests(): void {
  inMemoryState = { templates: [] };
}
export function __resetUserTemplatesListenersForTests(): void {
  listeners.clear();
}
export function __reloadUserTemplatesFromStorageForTests(storage: Storage): void {
  inMemoryState = namespace.load(storage);
}
